// Phase → display word, tone and pulse (ui-spec §3.7). The word shown is a plain display word; the raw phase always
// travels with it so the pill tooltip can say "phase: Succeeded". Three tones in lists (green, grey, brick red) plus
// warning; transitional phases use the neutral tone and pulse.

export type Tone = "neutral" | "success" | "warning" | "danger";

export interface StatusInput {
  kind?: string;
  phase?: string;
  reason?: string;
  stopReason?: string;
}

export interface StatusDisplay {
  word: string;
  tone: Tone;
  transitional: boolean;
  /** The API phase the word came from, for the tooltip. */
  phase: string;
  /** A reason worth showing after the word ("Suspended · InsufficientCredits"). */
  detail?: string;
}

export const MONEY_REASONS: ReadonlySet<string> = new Set([
  "InsufficientCredits",
  "BudgetExceeded",
  "MaxCostReached",
  "ArrearsOutstanding",
  "PaymentDisputed",
]);

const TRANSITIONAL = new Set([
  "Queued",
  "Pending",
  "Provisioning",
  "Starting",
  "Recovering",
  "Suspending",
  "Stopping",
  "Cancelling",
  "Terminating",
]);

const SYSTEM_WAITS = new Set(["BudgetWait", "CapacityWait", "QuotaWait", "NodeLost"]);
const REQUESTED_PAUSE_REASONS = new Set(["User", "Suspended"]);

export const TERMINAL_PHASES: ReadonlySet<string> = new Set(["Succeeded", "Failed", "Cancelled"]);

export function isTerminal(phase: string | undefined): boolean {
  return phase !== undefined && TERMINAL_PHASES.has(phase);
}

/** Per-kind words for kinds whose phases are not workload phases (§3.7 per-kind rows). */
const KIND_WORDS: Record<string, Record<string, { word: string; tone: Tone; transitional?: boolean }>> = {
  TopUp: {
    Pending: { word: "Waiting for payment", tone: "neutral", transitional: true },
    Succeeded: { word: "Paid", tone: "success" },
    Expired: { word: "Expired", tone: "neutral" },
    Failed: { word: "Failed", tone: "danger" },
  },
  Pool: {
    Pending: { word: "Waiting for a host", tone: "neutral", transitional: true },
    Ready: { word: "Ready", tone: "success" },
  },
  CloudAccount: {
    Pending: { word: "Finish setup", tone: "warning" },
    SetupRequired: { word: "Finish setup", tone: "warning" },
    Checking: { word: "Checking access", tone: "neutral", transitional: true },
    NeedsAttention: { word: "Needs attention", tone: "warning" },
    Ready: { word: "Connected", tone: "success" },
  },
  Connection: {
    Ready: { word: "Connected", tone: "success" },
  },
  InferenceEndpoint: {
    Running: { word: "Active", tone: "success" },
  },
  OAuthGrant: {
    Active: { word: "Active", tone: "success" },
    Expired: { word: "Expired", tone: "neutral" },
  },
  PoolAction: {
    Queued: { word: "Awaiting approval", tone: "neutral" },
  },
  // A WebhookEndpoint has no phase; the console derives one: Failing while the Healthy condition is False, Disabled
  // while `spec.enabled` is false. A delivery's phase is the delivery log's own.
  WebhookEndpoint: {
    Active: { word: "Active", tone: "success" },
    Failing: { word: "Failing", tone: "warning" },
    Disabled: { word: "Disabled", tone: "neutral" },
  },
  WebhookDelivery: {
    Pending: { word: "Retrying", tone: "neutral", transitional: true },
    Succeeded: { word: "Delivered", tone: "success" },
    Failed: { word: "Failed", tone: "danger" },
    Abandoned: { word: "Abandoned", tone: "neutral" },
  },
  // A Budget has no phase; the console derives one from its status (ui-spec §3.7: exceeded, at a threshold, else active).
  Budget: {
    Active: { word: "Active", tone: "success" },
    Threshold: { word: "Near limit", tone: "warning" },
    Exceeded: { word: "Exceeded", tone: "danger" },
  },
};

export function statusDisplay({ kind, phase, reason, stopReason }: StatusInput): StatusDisplay {
  const p = phase ?? "Unknown";
  const override = kind ? KIND_WORDS[kind]?.[p] : undefined;
  if (override) {
    return { word: override.word, tone: override.tone, transitional: override.transitional ?? false, phase: p };
  }
  switch (p) {
    case "Running":
      return { word: "Running", tone: "success", transitional: false, phase: p };
    case "Ready":
      return { word: "Ready", tone: "success", transitional: false, phase: p };
    case "Succeeded":
      return { word: "Completed", tone: "neutral", transitional: false, phase: p };
    case "Failed":
      return { word: "Failed", tone: "danger", transitional: false, phase: p, ...(reason ? { detail: reason } : {}) };
    case "Cancelled":
      return { word: "Cancelled", tone: "neutral", transitional: false, phase: p };
    case "Recovering":
      return { word: "Recovering", tone: "warning", transitional: true, phase: p };
    case "Waiting": {
      const tone: Tone = reason === "BudgetWait" ? "warning" : "neutral";
      return { word: "Waiting", tone, transitional: false, phase: p, ...(reason ? { detail: reason } : {}) };
    }
    case "Suspended": {
      const why = stopReason ?? reason;
      return why && !REQUESTED_PAUSE_REASONS.has(why)
        ? { word: "Suspended", tone: "warning", transitional: false, phase: p, detail: why }
        : { word: "Suspended", tone: "neutral", transitional: false, phase: p };
    }
    case "Stopped": {
      const why = stopReason ?? reason;
      if (why === "Idle") return { word: "Sleeping", tone: "neutral", transitional: false, phase: p };
      if (!why || why === "User") return { word: "Stopped", tone: "neutral", transitional: false, phase: p };
      const tone: Tone = MONEY_REASONS.has(why) ? "warning" : "neutral";
      return { word: "Stopped", tone, transitional: false, phase: p, detail: why };
    }
    default:
      return { word: p, tone: "neutral", transitional: TRANSITIONAL.has(p), phase: p };
  }
}

/** The field selector a status filter sends for a display word, the same thing `--field-selector` takes. */
export function statusSelector(word: string): string | undefined {
  switch (word) {
    case "Completed":
      return "status.phase=Succeeded";
    case "Sleeping":
      return "status.phase=Stopped,status.stopReason=Idle";
    case "Active":
      return "status.phase=Running";
    case "Connected":
    case "Verified":
      return "status.phase=Ready";
    default:
      return /^[A-Z][A-Za-z]+$/.test(word) ? `status.phase=${word}` : undefined;
  }
}

/** Whether the header shows the FailureCallout for this object (§3.4.10). */
export function needsFailureCallout(input: StatusInput): boolean {
  const { phase, reason, stopReason } = input;
  if (phase === "Failed") return true;
  const why = stopReason ?? reason;
  if (phase === "Suspended") return Boolean(why && !REQUESTED_PAUSE_REASONS.has(why));
  if (phase === "Waiting") return Boolean(why && (MONEY_REASONS.has(why) || SYSTEM_WAITS.has(why)));
  if (phase === "Stopped") return Boolean(why && why !== "User" && why !== "Idle");
  return false;
}
